import Phaser from 'phaser';
import type { ZoneDef } from '../layout/zones.js';
import { ChipStackVisual } from './ChipStackVisual.js';
import type { ShimmerClock } from './ShimmerClock.js';
import { attachPressGesture, type PressGestureHandle } from './pressGesture.js';

// All odds stacks offset below their parent stack (dy = 1, dx = 0).
const ODDS_OFFSET_DIRECTIONS: Record<string, { dx: number; dy: number } | undefined> = {
  pass:     { dx: 0, dy: 1 },
  dontPass: { dx: 0, dy: 1 },
  come:     { dx: 0, dy: 1 },
  dontCome: { dx: 0, dy: 1 },
};

export interface BetZoneEvents {
  onClick: (zone: BetZone) => void;
  onLongPress?: (zone: BetZone, pointer: Phaser.Input.Pointer) => void;
  /** Fires on pointerover (desktop only — gated by the hover/pointer media query). */
  onHover?: (zone: BetZone) => void;
  /** Fires on pointerout. Always fires when the corresponding onHover did. */
  onHoverEnd?: (zone: BetZone) => void;
}

export class BetZone extends Phaser.GameObjects.Container {
  private outline: Phaser.GameObjects.Graphics;
  private highlight: Phaser.GameObjects.Graphics;
  private label: Phaser.GameObjects.Text;
  private payoutLabel: Phaser.GameObjects.Text;
  private chipStack: ChipStackVisual;
  private oddsChipStack?: ChipStackVisual;
  private allowed = true;
  private currentBetCents = 0;
  private parentCentsState = 0;
  private oddsCentsState = 0;
  private polygonPoints: Phaser.Geom.Point[];
  private pressGesture?: PressGestureHandle;
  private breathingGlow?: Phaser.GameObjects.Graphics;
  private breathingTween?: Phaser.Tweens.Tween;

  constructor(
    scene: Phaser.Scene,
    public def: ZoneDef,
    events: BetZoneEvents,
    shimmerClock?: ShimmerClock,
  ) {
    super(scene, 0, 0);

    const W = scene.scale.width;
    const H = scene.scale.height;
    // Portrait viewports squeeze the landscape-tuned sm to ~0.3, leaving the
    // chip stack painfully small (~4px). Track the felt panel width (mainW = W
    // over 476.25 SVG units) and floor it so even narrow phones render chips
    // at thumb scale.
    const sm = H > W
      ? Math.max(0.85, W / 476.25)
      : Math.min(W / 1280, H / 800);
    const dpr = (scene.game.registry.get('dpr') as number | undefined) ?? 1;
    const polygon = new Phaser.Geom.Polygon(def.polygon);
    this.polygonPoints = polygon.points;
    const bounds = Phaser.Geom.Polygon.GetAABB(polygon);
    const cx = def.labelAnchor ? def.labelAnchor.x : bounds.x + bounds.width / 2;
    const cy = def.labelAnchor ? def.labelAnchor.y : bounds.y + bounds.height / 2;

    // Outline / labels / payout-hint visuals come from the SVG backdrop now,
    // so we only render zone-specific UI: hover highlight, chip stack, and
    // working marker. The outline + label fields are kept (invisible) so the
    // existing setLegality / setAlpha / flashWin code paths still work.
    this.outline = scene.add.graphics();
    this.outline.setVisible(false);
    this.add(this.outline);

    this.highlight = scene.add.graphics();
    this.highlight.fillStyle(0xffffff, 0.12);
    this.highlight.fillPoints(polygon.points, true);
    this.highlight.setVisible(false);
    this.add(this.highlight);

    this.label = scene.add.text(cx, cy, '', { fontSize: '1px' }).setOrigin(0.5).setVisible(false);
    this.add(this.label);
    this.payoutLabel = scene.add.text(cx, cy, '', { fontSize: '1px' }).setOrigin(0.5).setVisible(false);
    this.add(this.payoutLabel);

    // Chip stack rests at the zone's chipAnchor when set, else falls back to
    // the legacy "below the label" position. Anchor is the BOTTOM chip's
    // centre — chips stack upward from here.
    const stackAnchor = def.chipAnchor
      ? { x: def.chipAnchor.x, y: def.chipAnchor.y }
      : { x: cx, y: cy + 28 * sm };
    this.chipStack = new ChipStackVisual({
      scene, anchor: stackAnchor, dpr,
      chipRadius: Math.round(14 * sm),
      shimmerClock,
    });

    const oddsDir = ODDS_OFFSET_DIRECTIONS[def.type];
    if (oddsDir) {
      const chipRadius = Math.round(14 * sm);
      const delta = Math.round(chipRadius * 1.3);
      this.oddsChipStack = new ChipStackVisual({
        scene,
        anchor: { x: stackAnchor.x + oddsDir.dx * delta, y: stackAnchor.y + oddsDir.dy * delta },
        dpr,
        chipRadius,
        showBadge: false,
        shimmerClock,
      });
    }

    // Display-only zones (e.g. come-N number boxes that render an established
    // come bet) opt out of pointer wiring entirely so clicks fall through to
    // the underlying clickable zone (e.g. the place-N rect at the same x).
    if (def.interactive !== false) {
      this.setInteractive({
        hitArea: polygon,
        hitAreaCallback: Phaser.Geom.Polygon.Contains,
        useHandCursor: true,
      });

      const hasHover = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(hover: hover)').matches;
      if (hasHover) {
        this.on('pointerover', () => {
          if (this.allowed) this.highlight.setVisible(true);
          if (hasHover) events.onHover?.(this);
        });
        this.on('pointerout', () => {
          this.highlight.setVisible(false);
          if (hasHover) events.onHoverEnd?.(this);
        });
      }

      // Long-press ring centres on the chip stack. It needs to be big enough
      // that the edges remain visible past a thumb on mobile (~50px contact
      // patch), so the radius is much larger than the chip stack.
      this.pressGesture = attachPressGesture({
        scene,
        target: this,
        ringCenter: stackAnchor,
        ringRadius: Math.round(90 * sm),
        ringStroke: Math.max(6, Math.round(10 * sm)),
        // Always fire onClick so help mode can show explanations on illegal
        // zones too. Live bridges still gate placement via engine.placeBet,
        // which returns an Error for disallowed bets.
        onTap: () => events.onClick(this),
        onLongPress: () => events.onLongPress?.(this, scene.input.activePointer),
        canLongPress: () => this.allowed && this.currentBetCents > 0,
      });
    }

    scene.add.existing(this);
  }

  override preDestroy() {
    this.stopBreathingGlow();
    this.pressGesture?.destroy();
    delete this.pressGesture;
    this.chipStack.destroy();
    this.oddsChipStack?.destroy();
    super.preDestroy();
  }

  /**
   * Pulse a soft gold tint across the whole zone to cue the player that this
   * is the action they need to take next (currently used to highlight
   * pass / don't-pass during come-out when no line bet is on the layout).
   * Idempotent — calling while already running is a no-op.
   */
  startBreathingGlow() {
    if (this.breathingGlow) return;
    const g = this.scene.add.graphics();
    // Filled gold wash + faint edge for definition. Fill stays subdued so the
    // SVG felt text underneath remains legible while the pulse cycles.
    g.fillStyle(0xffd24a, 1);
    g.fillPoints(this.polygonPoints, true);
    g.lineStyle(3, 0xffd24a, 1);
    g.strokePoints(this.polygonPoints, true);
    g.setAlpha(0.12);
    this.add(g);
    this.breathingGlow = g;
    this.breathingTween = this.scene.tweens.add({
      targets: g,
      alpha: { from: 0.12, to: 0.45 },
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  stopBreathingGlow() {
    this.breathingTween?.stop();
    delete this.breathingTween;
    this.breathingGlow?.destroy();
    delete this.breathingGlow;
  }

  /**
   * Anchor at the left edge of the chip stack — winnings chips spawn here so
   * they appear "next to" the bet, then fly off to the bankroll pill.
   */
  getWinningsSpawnAnchor(chipRadius: number): { x: number; y: number } {
    const a = this.chipStack.getAnchor();
    return { x: a.x - chipRadius * 2, y: a.y };
  }

  /** Centre of the bottom chip — used by callers spawning transient stacks
   *  that slide to or from this zone (e.g. come-bet travel). */
  getChipStackAnchor(): { x: number; y: number } {
    return this.chipStack.getAnchor();
  }

  /** Returns the axis-aligned bounding box of the zone's polygon, in scene coords. */
  hoverAnchor(): { x: number; y: number; w: number; h: number } {
    const aabb = Phaser.Geom.Polygon.GetAABB(new Phaser.Geom.Polygon(this.polygonPoints));
    return { x: aabb.x, y: aabb.y, w: aabb.width, h: aabb.height };
  }

  /** This zone's per-chip radius — match it when spawning transient stacks
   *  so a slide-from / slide-to chip reads the same visual size as the
   *  destination's resting stack instead of overshooting into adjacent zones. */
  getChipRadius(): number {
    return this.chipStack.getChipRadius();
  }

  setChipStackCents(cents: number) {
    this.currentBetCents = cents;
    this.parentCentsState = cents;
    // Alternate hit targets (e.g. small E/C circles) explicitly opt out of
    // rendering chip stacks; the canonical zone shows the total instead.
    if (this.def.showChipStack === false) {
      this.chipStack.setCents(0);
      return;
    }
    this.chipStack.setCents(cents);
    this.refreshMergedBadge();
  }

  setOddsChipStackCents(cents: number) {
    if (!this.oddsChipStack) return;
    this.oddsCentsState = cents;
    this.oddsChipStack.setCents(cents);
    this.refreshMergedBadge();
  }

  private refreshMergedBadge() {
    if (!this.oddsChipStack) return;
    if (this.parentCentsState > 0 && this.oddsCentsState > 0) {
      const parent$ = Math.floor(this.parentCentsState / 100);
      const odds$ = Math.floor(this.oddsCentsState / 100);
      this.chipStack.setBadgeOverride(`$${parent$}/$${odds$}`);
    } else {
      this.chipStack.setBadgeOverride(null);
    }
  }

  getOddsChipStackAnchor(): { x: number; y: number } {
    return this.oddsChipStack?.getAnchor() ?? this.chipStack.getAnchor();
  }

  startHinting() { this.pressGesture?.startHinting(); }
  stopHinting()  { this.pressGesture?.stopHinting(); }

  setLegality(allowed: boolean, _reason?: string) {
    this.allowed = allowed;
    this.label.setAlpha(allowed ? 1 : 0.35);
    this.payoutLabel.setAlpha(allowed ? 1 : 0.35);
    this.outline.setAlpha(allowed ? 1 : 0.35);
    if (this.input) this.input.cursor = allowed ? 'pointer' : 'default';
  }

  flashWin() {
    const flash = this.scene.add.graphics();
    flash.fillStyle(0xffd24a, 0.6);
    flash.fillPoints(this.polygonPoints, true);
    flash.lineStyle(3, 0xffd24a, 1);
    flash.strokePoints(this.polygonPoints, true);
    flash.setAlpha(0);
    this.add(flash);
    this.scene.tweens.add({
      targets: flash,
      alpha: { from: 0, to: 1 },
      duration: 300,
      yoyo: true,
      onComplete: () => flash.destroy(),
    });
  }

  /** Brief red-border flash to communicate "this click was rejected" — used
   *  when the zone is at table max so the user sees *why* the chip didn't
   *  land instead of a silent no-op. No fill so it reads as denial, not win. */
  flashRejected() {
    const flash = this.scene.add.graphics();
    flash.lineStyle(3, 0xff5d5d, 1);
    flash.strokePoints(this.polygonPoints, true);
    flash.setAlpha(0);
    this.add(flash);
    this.scene.tweens.add({
      targets: flash,
      alpha: { from: 0, to: 1 },
      duration: 180,
      yoyo: true,
      repeat: 1,
      onComplete: () => flash.destroy(),
    });
  }
}

